import { createContext, useCallback, useContext, useState } from "react";

interface ChatContextValue {
  openChatWithMessage: (message: string) => void;
  openChatForWidget: (widgetId: string, widgetTitle: string) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  initialMessage: string | null;
  clearInitialMessage: () => void;
  activeWidgetId: string | null;
  activeWidgetTitle: string | null;
  clearWidgetContext: () => void;
  activeThreadId: string | null;
  setActiveThreadId: (id: string | null) => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [initialMessage, setInitialMessage] = useState<string | null>(null);
  const [activeWidgetId, setActiveWidgetId] = useState<string | null>(null);
  const [activeWidgetTitle, setActiveWidgetTitle] = useState<string | null>(null);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);

  const openChatWithMessage = useCallback((message: string) => {
    setInitialMessage(message);
    setIsOpen(true);
  }, []);

  const openChatForWidget = useCallback((widgetId: string, widgetTitle: string) => {
    setActiveWidgetId(widgetId);
    setActiveWidgetTitle(widgetTitle);
    setIsOpen(true);
  }, []);

  const clearInitialMessage = useCallback(() => {
    setInitialMessage(null);
  }, []);

  const clearWidgetContext = useCallback(() => {
    setActiveWidgetId(null);
    setActiveWidgetTitle(null);
  }, []);

  return (
    <ChatContext.Provider
      value={{
        openChatWithMessage,
        openChatForWidget,
        isOpen,
        setIsOpen,
        initialMessage,
        clearInitialMessage,
        activeWidgetId,
        activeWidgetTitle,
        clearWidgetContext,
        activeThreadId,
        setActiveThreadId
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChatContext() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within ChatProvider");
  }
  return context;
}
